import { BodyText, Caption, FieldsetCard } from '@if/mui'
import { Box, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material'
import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

interface Props {
  onChange: (value: boolean) => void
  disabled?: boolean
}

const DIMMED_OPACITY = 0.35
const dimmed = (active: boolean) => ({ opacity: active ? 1 : DIMMED_OPACITY, transition: 'opacity 0.2s' })

interface YesNoToggleProps {
  value: boolean
  onChange: (value: boolean) => void
  disabled?: boolean
}

const YesNoToggle = ({ value, onChange, disabled = false }: YesNoToggleProps) => {
  const { t } = useTranslation()

  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      color="primary"
      disabled={disabled}
      value={value ? 'yes' : 'no'}
      onChange={(_, newValue: string | null) => {
        if (newValue !== null) onChange(newValue === 'yes')
      }}
      sx={{ flexShrink: 0 }}
    >
      <ToggleButton value="yes">{t('common.yes')}</ToggleButton>
      <ToggleButton value="no">{t('common.no')}</ToggleButton>
    </ToggleButtonGroup>
  )
}

// Two-question filter flow: the derived flag sent to the form is (question 1 && question 2).
// Answers are kept locally because only the derived isSymTechPrime is persisted.
const SymTechPrimeQuestion = ({ onChange, disabled = false }: Props) => {
  const { t } = useTranslation()
  const [isSymTechProduct, setIsSymTechProduct] = useState(false)
  const [isPreferredRateProgram, setIsPreferredRateProgram] = useState(false)

  const handleSymTechProductChanged = (value: boolean) => {
    setIsSymTechProduct(value)
    const preferredRate = value ? isPreferredRateProgram : false
    if (!value) setIsPreferredRateProgram(false)
    onChange(value && preferredRate)
  }

  const handlePreferredRateChanged = (value: boolean) => {
    setIsPreferredRateProgram(value)
    onChange(isSymTechProduct && value)
  }

  return (
    <FieldsetCard title={t('editCreditApplication.symTechPrime.title')}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
          <BodyText>
            <Trans i18nKey="editCreditApplication.symTechPrime.symTechProductQuestion" />
          </BodyText>
          <YesNoToggle value={isSymTechProduct} onChange={handleSymTechProductChanged} disabled={disabled} />
        </Stack>

        <Stack
          direction="row"
          spacing={2}
          justifyContent="space-between"
          alignItems="center"
          sx={dimmed(isSymTechProduct)}
        >
          <Box>
            <BodyText>
              <Trans i18nKey="editCreditApplication.symTechPrime.preferredRateQuestion" />
            </BodyText>
            <Caption>{t('editCreditApplication.symTechPrime.preferredRateAvailability')}</Caption>
            <Box component="ul" sx={{ my: 0, pl: 3 }}>
              <li>
                <Caption>
                  <Trans i18nKey="editCreditApplication.symTechPrime.preferredRateManufacturers" />
                </Caption>
              </li>
              <li>
                <Caption>
                  <Trans i18nKey="editCreditApplication.symTechPrime.preferredRateInterest" />
                </Caption>
              </li>
            </Box>
          </Box>
          <YesNoToggle
            value={isPreferredRateProgram}
            onChange={handlePreferredRateChanged}
            disabled={disabled || !isSymTechProduct}
          />
        </Stack>

        <Caption sx={dimmed(isSymTechProduct && isPreferredRateProgram)}>
          {t('editCreditApplication.symTechPrime.note')}
        </Caption>
      </Stack>
    </FieldsetCard>
  )
}

export default SymTechPrimeQuestion
